import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import BarLoader from "./../common/Loader/BarLoader";
import ConversationHeader from "./components/messageHeader/conversationHeader";
import ListConversations from "./components/listConversations/listConversations";
import NewMessageHeader from "./components/newMessageHeader/newMessageHeader";
import ConversationTextField from "./components/conversationTextField/conversationTextField";
import MappedConversation from "./components/mappedConversation/MappedConversation";
import EmptyInbox from "./components/emptyBox/EmptyInbox";
import { STANDARD_HOOKS, CLIENTS } from "../../api/config/config";
import generateUniqueName from "../../api/utils/generateUniqueName";
import { useInitializeNewConversation } from "../../hooks/useInitializeNewConversation";
import { useConversations } from "./../../hooks/useConversations";
import { parseMessagesByDate } from "../utils/parseMessageByDate";
import InboxTypes from "../utils/enums/inboxTypes.enum";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const CONVERSATION_STATUS_ENUM = {
  NOT_EXIST: 0,
  EXIST: 1,
  FAILS: 2
};
const Message = _ref => {
  var _activeConversation$s, _activeConversation$s2;
  let {
    currentEmployeeId,
    TEXT_AREA_ID,
    toast,
    messageCenterLogger,
    source,
    options
  } = _ref;
  const [pageNumber, setPageNumber] = useState(1);
  const {
    archiveEnabled,
    headerLinksEnabled
  } = options;
  const {
    initialized,
    rawConversationList,
    conversationList,
    loadMoreConversations,
    moreConversationsToBeLoaded,
    isPopulating,
    inboxType,
    setInboxType,
    activeConversation,
    setActiveConversation,
    setShowNewMessage,
    showNewMessage,
    uniqueName,
    setUniqueName,
    resetState,
    conversationPageSize,
    conversationsLength
  } = useConversations({
    messageCenterLogger,
    archiveEnabled,
    pageNumber
  });
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [notMappedConversationId, setNotMappedConversationId] = useState(null);
  const [pendingMessage, setPendingMessage] = useState(null);
  const [recipientId, setRecipientId] = useState(null);
  const textAreaRef = useRef();
  const {
    usePost
  } = STANDARD_HOOKS;
  const {
    apiMessagingService,
    twilioClient
  } = CLIENTS;
  const {
    postFn
  } = usePost();
  const subjectId = searchParams.get('subject'); //'case id' or 'referral id'
  const subjectType = searchParams.get('subjectType'); //Switch with 'referral' or 'case'

  const {
    topicTitle,
    personName,
    personId
  } = useInitializeNewConversation({
    subjectId: subjectId,
    subjectType: subjectType,
    source: source
  });

  // Messages Mapping & Active Conversation Population
  const switchActiveConversation = useCallback(async selectedConversation => {
    if ((selectedConversation === null || selectedConversation === void 0 ? void 0 : selectedConversation.sid) !== (activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.sid)) {
      var _textAreaRef$current;
      const textInputValue = (_textAreaRef$current = textAreaRef.current) === null || _textAreaRef$current === void 0 ? void 0 : _textAreaRef$current.getTextInputValue();
      if (textInputValue !== null && textInputValue !== void 0 && textInputValue.length && window.confirm('Are you sure you want to leave this message?') || !textInputValue) {
        try {
          const {
            items
          } = await selectedConversation.getMessages();
          selectedConversation.messagesByDate = parseMessagesByDate(items);
          selectedConversation.totalMessages = items.length - 1;
          setActiveConversation(selectedConversation);
          setUniqueName(generateUniqueName({
            participantIds: selectedConversation.participants.map(part => part.id),
            subjectType: subjectType,
            subjectId: subjectId
          }));
          localStorage.setItem(TEXT_AREA_ID, '');
          setShowNewMessage(false);
          selectedConversation.setAllMessagesRead();
        } catch (error) {
          messageCenterLogger('Error while switching active conversation', {
            selectedConversation
          }, error);
        }
      }
    }
  }, [TEXT_AREA_ID, activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.sid, subjectId, subjectType]);
  const getConversationAndSend = async message => {
    if (!uniqueName || uniqueName.length <= 0) {
      // If uniqueName is not set yet, store the message and wait
      setPendingMessage(message);
      return;
    }
    let newConversation;
    // If there is not an active conversation (user is trying to create a new conversation)
    if (!(activeConversation !== null && activeConversation !== void 0 && activeConversation.sid)) {
      try {
        newConversation = await createConversation();
      } catch (error) {
        messageCenterLogger('Failed to create conversation before sending a message', {
          message
        }, error);
        return;
      }
    }
    await sendMessage(message, newConversation || activeConversation);
  };
  const createConversation = async () => {
    const exists = await checkConversationExists();

    // If the conversation already exists, return it
    if (exists.status === CONVERSATION_STATUS_ENUM.EXIST) {
      return exists.conversation;
    }

    // If the conversation does not exist, create a new one
    try {
      const {
        data: {
          conversation_sid
        }
      } = await postFn({
        unique_name: uniqueName,
        subject_type: subjectType,
        subject_id: subjectId,
        participants: [currentEmployeeId, recipientId]
      }, {
        api: apiMessagingService,
        url: "/conversations"
      });
      const conversation = await twilioClient.getConversationBySid(conversation_sid);
      return conversation;
    } catch (error) {
      if (error.response && error.response.status === 422) {
        toast.error("We cant't send your message right now. Please try again later. If the issue persist, contact the Unite Us Support team.");
        messageCenterLogger('Validation failed: Conversation attributes are not valid or User conversation limit exceeded', {
          uniqueName,
          subjectType,
          subjectId,
          error: error.response.data
        });
      } else {
        messageCenterLogger('Failed to create a conversation', {
          uniqueName,
          subjectType,
          subjectId
        }, error);
      }
      throw error;
    }
  };
  const sendMessage = async (message, conversationToMessage) => {
    try {
      if (conversationToMessage) {
        const sentMessage = await conversationToMessage.sendMessage(message);
        conversationToMessage.updateLastReadMessageIndex(sentMessage);
        if (activeConversation) {
          navigateToAnotherConversation(activeConversation);
          setPageNumber(1);
        } else {
          setNotMappedConversationId(conversationToMessage.sid);
        }
        localStorage.setItem(TEXT_AREA_ID, '');
      }
    } catch (error) {
      toast.error(404, 'Sorry we are unable to send your message. Please try again later. If the issue persists, please contact the Unite Us Help Center.');
      messageCenterLogger('Message failed to be sent', {
        message
      }, error);
      console.error(error);
    }
  };
  const checkConversationExists = useCallback(async () => {
    try {
      // Avoid twilioClient calls with unset uniqueName
      if (!uniqueName) return {
        status: CONVERSATION_STATUS_ENUM.FAILS
      };
      const conversation = await twilioClient.getConversationByUniqueName(uniqueName);
      return {
        conversation: conversation,
        status: CONVERSATION_STATUS_ENUM.EXIST
      };
    } catch (error) {
      if (error.message === 'Not Found') {
        return {
          status: CONVERSATION_STATUS_ENUM.NOT_EXIST
        };
      }
      messageCenterLogger('TwilioClient failed to retrieve conversation', {
        uniqueName
      }, error);
      return {
        status: CONVERSATION_STATUS_ENUM.FAILS
      };
    }
  }, [twilioClient, uniqueName]);
  const onEmployeeChange = recipientId => {
    setRecipientId(recipientId);
    const newUniqueName = generateUniqueName({
      participantIds: [currentEmployeeId, recipientId],
      subjectType: subjectType,
      subjectId: subjectId
    });
    setUniqueName(newUniqueName);
  };
  useEffect(() => {
    const conversationExist = async () => {
      try {
        const exists = await checkConversationExists();
        if (exists.status !== CONVERSATION_STATUS_ENUM.FAILS && uniqueName.length > 0) {
          if (exists.status === CONVERSATION_STATUS_ENUM.EXIST) {
            setNotMappedConversationId(exists.conversation.sid);
          } else {
            setShowNewMessage(true);
          }
        }
      } catch (error) {
        messageCenterLogger('Failed to check if conversation exists', {
          uniqueName
        }, error);
      }
    };
    if (!activeConversation) {
      conversationExist();
    }
  }, [activeConversation, checkConversationExists, uniqueName]);
  useEffect(() => {
    if (!activeConversation && notMappedConversationId) {
      //not active conversation but trying to open one
      const activeConversationInList = rawConversationList.find(convo => convo.sid === notMappedConversationId);
      const openNewConversation = async () => {
        try {
          setInboxType(activeConversationInList.state.current === "active" ? InboxTypes.INBOX : InboxTypes.ARCHIVE);
          navigateToAnotherConversation(activeConversationInList);
          setShowNewMessage(false);
          setNotMappedConversationId(null);
        } catch (error) {
          messageCenterLogger('Failed to open a new conversation', {
            activeConversationInList
          }, error);
        }
      };
      if (activeConversationInList) openNewConversation();
    }
  }, [notMappedConversationId, conversationList, activeConversation, rawConversationList]);
  const navigateToAnotherConversation = conversation => {
    try {
      const fullPath = location.pathname;

      /*
        This logic is necessary because frontend and emr uses different paths:
        frontend: /message-center and emr: /c91f42300c/2/message-center
      */
      // Check if fullPath contains "/message-center"
      const index = fullPath.indexOf('/message-center');
      if (index !== -1) {
        const base = fullPath.substring(0, index + '/message-center'.length);
        const remainingPath = fullPath.substring(index + '/message-center'.length);

        // If there's a segment after "/message-center", replace it. Else, append the new conversation.sid.
        if (remainingPath.startsWith('/')) {
          const segments = remainingPath.split('/');
          segments[1] = conversation.sid; // replace the segment after "/message-center"
          navigate(base + segments.join('/'));
        } else {
          navigate("".concat(base, "/").concat(conversation.sid));
        }
      } else {
        // Handle cases where "/message-center" isn't present, if needed
        navigate("".concat(fullPath, "/message-center/").concat(conversation.sid));
      }
    } catch (error) {
      messageCenterLogger('Failed to navigate to another conversation', {
        conversation
      }, error);
    }
  };
  useEffect(() => {
    const actualRoute = window.location.pathname;
    const routeSplitted = actualRoute.split('/');
    if (subjectId && subjectType) {
      setShowNewMessage(true);
    } else if (!subjectId && !subjectType && routeSplitted[routeSplitted.length - 1] === 'message-center') {
      setUniqueName("");
      setShowNewMessage(false);
      setActiveConversation(null);
    } else {
      const conversationIdFromRoute = routeSplitted[routeSplitted.length - 1];
      if (isConversationSidValid(conversationIdFromRoute)) {
        const conversation = rawConversationList.find(convo => convo.sid === conversationIdFromRoute);
        if (conversation) {
          setInboxType(conversation.state.current === "active" ? InboxTypes.INBOX : InboxTypes.ARCHIVE);
          switchActiveConversation(conversation);
        }
      }
    }
  }, [navigate, conversationList, subjectId, subjectType, setShowNewMessage]);
  const isConversationSidValid = id => {
    const regex = /^CH[A-Za-z0-9]{32}$/;
    return regex.test(id);
  };
  useEffect(() => {
    // Check if there's a pending message and uniqueName is set
    if (pendingMessage) {
      const regeneratedUniqueName = generateUniqueName({
        participantIds: [currentEmployeeId, recipientId],
        subjectType: subjectType,
        subjectId: subjectId
      });
      if (regeneratedUniqueName && regeneratedUniqueName.length > 1) {
        setUniqueName(regeneratedUniqueName);
        // Logic to send the message...
        getConversationAndSend(pendingMessage);
        // Reset the pending message state
        setPendingMessage(null);
      }
    }
  }, [pendingMessage, uniqueName]);
  const onChangeInboxType = inboxType => {
    setInboxType(inboxType);
    navigate('/message-center');
    setPageNumber(1);
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "p-5 flex flex-row w-full",
    style: {
      height: '85vh'
    },
    children: [/*#__PURE__*/_jsx("div", {
      className: "messages-index bg-white p-6 rounded-md shadow-md shadow-slate-500",
      style: {
        width: '467px'
      },
      children: isPopulating ? /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(BarLoader, {
          className: "w-full mb-8",
          tall: true
        })
      }) : /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(ListConversations, {
          activeConversationId: activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.sid,
          conversations: conversationList,
          rawConversationList: rawConversationList,
          onConversationClick: conversation => navigateToAnotherConversation(conversation),
          currentEmployeeId: currentEmployeeId,
          moreConversationsToBeLoaded: moreConversationsToBeLoaded,
          loadMoreConversations: loadMoreConversations,
          inboxType: inboxType,
          onChangeInboxType: onChangeInboxType,
          archiveEnabled: archiveEnabled,
          pagerSettings: {
            show: conversationsLength > conversationPageSize,
            pageNumber,
            setPageNumber: setPageNumber,
            pageSize: conversationPageSize,
            totalItemCount: conversationsLength,
            totalPageCount: Math.ceil(conversationsLength / conversationPageSize)
          }
        })
      })
    }), (showNewMessage || (activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.sid)) && /*#__PURE__*/_jsxs("div", {
      className: "messages-wrapper relative flex-1 ml-5 mr-10 bg-white p-6 rounded-md shadow-md shadow-slate-500",
      children: [showNewMessage ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(NewMessageHeader, {
          subjectId: subjectId,
          subjectType: subjectType,
          onEmployeeChange: onEmployeeChange,
          source: source,
          headerLinksEnabled: headerLinksEnabled,
          topicTitle: topicTitle,
          personName: personName,
          personId: personId,
          apiMessagingService: apiMessagingService,
          setShowNewMessage: setShowNewMessage,
          toast: toast,
          navigate: navigate
        }), /*#__PURE__*/_jsx("div", {
          className: "empty-chat block"
        })]
      }) : /*#__PURE__*/_jsx(_Fragment, {
        children: (activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.sid) && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ConversationHeader, {
            currentConversation: activeConversation,
            currentEmployeeId: currentEmployeeId,
            source: source,
            headerLinksEnabled: headerLinksEnabled,
            inboxType: inboxType,
            setInboxType: setInboxType,
            setShowNewMessage: setShowNewMessage,
            resetState: resetState,
            messageCenterLogger: messageCenterLogger,
            toast: toast,
            archiveEnabled: archiveEnabled
          }), /*#__PURE__*/_jsx(MappedConversation, {
            conversation: activeConversation,
            currentEmployeeId: currentEmployeeId,
            twilioClient: twilioClient,
            inboxType: inboxType
          })]
        })
      }), !showNewMessage && (activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.sid) && (activeConversation === null || activeConversation === void 0 || (_activeConversation$s = activeConversation.state) === null || _activeConversation$s === void 0 ? void 0 : _activeConversation$s.current) === 'inactive' && inboxType === InboxTypes.ARCHIVE && /*#__PURE__*/_jsxs("p", {
        className: "w-full h-24 m-0 flex flex-col items-center absolute inset-x-0 bottom-0 font-regular-font float-left italic text-mc-gray text-center text-14px",
        children: ["This conversation was archived on ", new Date(activeConversation.state.dateUpdated).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }), "."]
      }), (showNewMessage || (activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.sid) && (activeConversation === null || activeConversation === void 0 || (_activeConversation$s2 = activeConversation.state) === null || _activeConversation$s2 === void 0 ? void 0 : _activeConversation$s2.current) === 'active') && /*#__PURE__*/_jsx(ConversationTextField, {
        getConversationAndSend: getConversationAndSend,
        ref: textAreaRef,
        textAreaId: TEXT_AREA_ID,
        initialValue: localStorage.getItem(TEXT_AREA_ID),
        recipientId: recipientId,
        showNewMessage: showNewMessage,
        readOnly: activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.readOnly
      }, activeConversation === null || activeConversation === void 0 ? void 0 : activeConversation.sid)]
    }), initialized && !isPopulating && conversationList.length === 0 && !showNewMessage && /*#__PURE__*/_jsx(EmptyInbox, {
      inboxType: inboxType
    })]
  });
};
export default Message;
//# sourceMappingURL=Message.js.map